import { Box, CircularProgress } from "@mui/material"
import { AddOutlined, UploadFileOutlined } from "@mui-symbols-material/w300"
import { useParams } from "@tanstack/react-router"
import type { ReactElement } from "react"
import { useState } from "react"

import { useGoodsReceiptContext } from "../../contexts"
import { useGoodsReceiptDocuments } from "../../hooks/useGoodsReceiptDocuments"

import { GoodsReceiptFileFormDialog } from "./GoodsReceiptFileFormDialog"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import { ConfirmationDialog } from "@/components/common/Modals"
import { FilesTable } from "@/components/Tables/Aeros/components/FilesTable"
import { useFileColumns } from "@/components/Tables/Aeros/hooks/useFileColumns"
import { PermissionActionType } from "@/constants/permissions"
import { ReferenceTableName, type SignedUrl } from "@/graphql/codegen/graphql"
import { useFilesRemove } from "@/hooks/useFilesRemove"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { NotificationType } from "@/schemas/notification"
import { GOODS_RECEIPT_DOCUMENT_TYPE_TO_CHIP_MAP } from "@/screens/WarehouseReceiving/constants"
import { getGoodsReceiptDocumentSourceLabel } from "@/screens/WarehouseReceiving/utils"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export const GoodsReceiptUploadsTab = (): ReactElement => {
  const { goodsReceiptId } = useParams({ strict: false })
  const { goodsReceipt } = useGoodsReceiptContext()
  const { hasPermissions } = usePermissionManager()
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [deletingDocument, setDeletingDocument] = useState<SignedUrl>()

  const { documents, isFetching, refetch } = useGoodsReceiptDocuments({ goodsReceipt })
  const { removeSingleFile } = useFilesRemove(() => refetch(true))

  const canMaintain = hasPermissions([PermissionActionType.WAREHOUSE_RECEIVING_MAINTENANCE])
  const hasDocuments = documents.length > 0

  const columns = useFileColumns({
    typeToChip: GOODS_RECEIPT_DOCUMENT_TYPE_TO_CHIP_MAP,
    canDelete: (document) => canMaintain && document.file?.referenceTableName !== ReferenceTableName.Order,
    getSourceLabel: getGoodsReceiptDocumentSourceLabel,
    onDelete: setDeletingDocument,
  })

  const handleDeleteConfirm = async (): Promise<void> => {
    const fileId = deletingDocument?.file?.fileId
    setDeletingDocument(undefined)

    if (!fileId) return

    const isDeleted = await removeSingleFile(fileId)
    notify(
      isDeleted
        ? { type: NotificationType.Success, message: "Goods receipt file was removed successfully" }
        : { type: NotificationType.Error, message: "Goods receipt file removing failed" }
    )
  }

  if (!hasDocuments && isFetching) {
    return (
      <Box className='flex h-full items-center justify-center'>
        <CircularProgress size={48} />
      </Box>
    )
  }

  return (
    <>
      <Box className='h-full'>
        {hasDocuments ? (
          <Box className='flex h-full flex-col'>
            {canMaintain && (
              <Button
                color='primary'
                size='small'
                appearance='outlined'
                className='mb-4 self-start'
                startIcon={<UploadFileOutlined />}
                onClick={() => setIsFormOpen(true)}
                data-testid='add-file-button'
              >
                Upload
              </Button>
            )}
            <FilesTable data={documents} isLoading={isFetching} columns={columns} />
          </Box>
        ) : (
          <InformationMissingAlert
            description='No documents have been uploaded yet. Once you add some, you will be able to see them here.'
            {...(canMaintain && {
              title: "Start by uploading documents",
              action: (
                <Button
                  color='inherit'
                  size='small'
                  appearance='outlined'
                  startIcon={<AddOutlined />}
                  onClick={() => setIsFormOpen(true)}
                  data-testid='add-file-button'
                >
                  New Upload
                </Button>
              ),
            })}
            data-testid='goods-receipt-file-alert'
          />
        )}
      </Box>
      {isFormOpen && (
        <GoodsReceiptFileFormDialog
          onClose={() => setIsFormOpen(false)}
          onRefresh={refetch}
          goodsReceiptId={goodsReceiptId}
        />
      )}
      {deletingDocument && (
        <ConfirmationDialog
          title='Remove File'
          content='Are you sure you want to remove this file?'
          closeLabel='Cancel'
          confirmLabel='Yes, Remove'
          isWarning
          onClose={() => setDeletingDocument(undefined)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  )
}
